<script setup lang="ts">
import { computed } from 'vue'
import { Head, Link } from '@inertiajs/vue3'
import { useFormatTime } from '@/Composables/useFormatTime'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faShredder, faClipboardList } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
library.add(faShredder, faClipboardList)

const props = defineProps<{
    title: string
    user: { name: string; role: string }
    stats?: { label: string; value: number; color: string }[]
    active_manufactures?: {
        slug: string
        reference: string
        type: string | null
        customer: string | null
        started_at: string
    }[]
    queue_orders?: {
        slug: string
        reference: string
        type: string | null
        customer: string | null
        created_at: string
    }[]
}>()

const colorClass: Record<string, { bg: string; text: string; border: string }> = {
    sky:     { bg: 'bg-sky-50',     text: 'text-sky-600',     border: 'border-sky-200' },
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-600',   border: 'border-amber-200' },
    indigo:  { bg: 'bg-indigo-50',  text: 'text-indigo-600',  border: 'border-indigo-200' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
}

const typeMeta: Record<string, { label: string; bg: string; text: string }> = {
    flexo: { label: 'Flexo', bg: 'bg-blue-50',  text: 'text-blue-600' },
    seng:  { label: 'Seng',  bg: 'bg-amber-50', text: 'text-amber-600' },
}

const roleLabel: Record<string, string> = {
    super_admin: 'Super Admin',
    kasir:       'Kasir',
    operator:    'Operator',
    layout:      'Layout',
}

const canViewManufacture = computed(() => ['super_admin', 'operator'].includes(props.user.role))

const greeting = (() => {
    const h = new Date().getHours()
    if (h < 11) return 'Selamat pagi'
    if (h < 15) return 'Selamat siang'
    if (h < 18) return 'Selamat sore'
    return 'Selamat malam'
})()
</script>

<template>
    <Head :title="title" />

    <div class="px-6 py-8 max-w-6xl mx-auto space-y-8">

        <!-- Header -->
        <div class="flex items-end justify-between">
            <div>
                <p class="text-sm text-gray-400">{{ greeting }},</p>
                <h1 class="text-2xl font-bold text-gray-800">{{ user.name }}</h1>
                <span class="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full bg-sky-100 text-sky-600">
                    {{ roleLabel[user.role] ?? user.role }}
                </span>
            </div>
            <p class="text-sm text-gray-400 hidden sm:block">
                {{ new Date().toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) }}
            </p>
        </div>

        <!-- Stats -->
        <div v-if="stats?.length" class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div
                v-for="stat in stats"
                :key="stat.label"
                class="rounded-xl border px-5 py-4"
                :class="[colorClass[stat.color]?.bg, colorClass[stat.color]?.border]"
            >
                <p class="text-xs font-medium text-gray-500">{{ stat.label }}</p>
                <p class="mt-1 text-3xl font-bold" :class="colorClass[stat.color]?.text">{{ stat.value }}</p>
            </div>
        </div>

        <div class="grid md:grid-cols-2 gap-6">

            <!-- Queue Orders -->
            <div v-if="queue_orders">
                <div class="flex items-center justify-between mb-3">
                    <h2 class="font-semibold text-gray-700 flex items-center gap-2">
                        <FontAwesomeIcon icon="fal fa-clipboard-list" class="text-amber-400" fixed-width aria-hidden="true" />
                        Antrian Produksi
                    </h2>
                    <Link v-if="canViewManufacture" :href="route('manufacture.queue.index')" class="text-xs text-sky-500 hover:underline">Lihat semua</Link>
                </div>
                <div class="overflow-hidden rounded-xl border border-amber-100 bg-white">
                    <table class="min-w-full divide-y divide-gray-100 text-sm">
                        <thead class="bg-amber-50 text-xs text-gray-500">
                            <tr>
                                <th class="px-4 py-2.5 text-left font-medium">No. Order</th>
                                <th class="px-4 py-2.5 text-left font-medium">Tipe</th>
                                <th class="px-4 py-2.5 text-left font-medium">Pelanggan</th>
                                <th class="px-4 py-2.5 text-left font-medium hidden sm:table-cell">Tgl. Masuk</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-50">
                            <tr v-for="order in queue_orders" :key="order.slug" class="hover:bg-gray-50 transition">
                                <td class="px-4 py-2.5 font-medium text-gray-700">{{ order.reference }}</td>
                                <td class="px-4 py-2.5">
                                    <span
                                        v-if="order.type && typeMeta[order.type]"
                                        class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                                        :class="[typeMeta[order.type].bg, typeMeta[order.type].text]"
                                    >
                                        {{ typeMeta[order.type].label }}
                                    </span>
                                    <span v-else class="text-gray-300">-</span>
                                </td>
                                <td class="px-4 py-2.5 text-gray-600">{{ order.customer ?? '-' }}</td>
                                <td class="px-4 py-2.5 text-gray-400 text-xs hidden sm:table-cell">{{ useFormatTime(order.created_at) }}</td>
                            </tr>
                            <tr v-if="!queue_orders.length">
                                <td colspan="4" class="px-4 py-6 text-center text-gray-400">Tidak ada antrian</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Active Manufactures -->
            <div v-if="active_manufactures">
                <div class="flex items-center justify-between mb-3">
                    <h2 class="font-semibold text-gray-700 flex items-center gap-2">
                        <FontAwesomeIcon icon="fal fa-shredder" class="text-indigo-400" fixed-width aria-hidden="true" />
                        Sedang Diproduksi
                    </h2>
                    <Link v-if="canViewManufacture" :href="route('manufacture.manufactures.index')" class="text-xs text-sky-500 hover:underline">Lihat semua</Link>
                </div>
                <div class="overflow-hidden rounded-xl border border-indigo-100 bg-white">
                    <table class="min-w-full divide-y divide-gray-100 text-sm">
                        <thead class="bg-indigo-50 text-xs text-gray-500">
                            <tr>
                                <th class="px-4 py-2.5 text-left font-medium">No. Produksi</th>
                                <th class="px-4 py-2.5 text-left font-medium">Tipe</th>
                                <th class="px-4 py-2.5 text-left font-medium">Pelanggan</th>
                                <th class="px-4 py-2.5 text-left font-medium hidden sm:table-cell">Mulai</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-50">
                            <tr v-for="m in active_manufactures" :key="m.slug" class="hover:bg-gray-50 transition">
                                <td class="px-4 py-2.5 font-medium">
                                    <Link v-if="canViewManufacture" :href="route('manufacture.manufactures.show', m.slug)" class="text-indigo-600 hover:underline">
                                        {{ m.reference }}
                                    </Link>
                                    <span v-else class="text-gray-700">{{ m.reference }}</span>
                                </td>
                                <td class="px-4 py-2.5">
                                    <span
                                        v-if="m.type && typeMeta[m.type]"
                                        class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                                        :class="[typeMeta[m.type].bg, typeMeta[m.type].text]"
                                    >
                                        {{ typeMeta[m.type].label }}
                                    </span>
                                    <span v-else class="text-gray-300">-</span>
                                </td>
                                <td class="px-4 py-2.5 text-gray-600">{{ m.customer ?? '-' }}</td>
                                <td class="px-4 py-2.5 text-gray-400 text-xs hidden sm:table-cell">{{ useFormatTime(m.started_at) }}</td>
                            </tr>
                            <tr v-if="!active_manufactures.length">
                                <td colspan="4" class="px-4 py-6 text-center text-gray-400">Tidak ada produksi aktif</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    </div>
</template>
